import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isSaturday = (d) => d.getDay() === 6;

const STATUS_STYLE = {
  pending: "text-yellow-400 border-yellow-400/40",
  confirmed: "text-green-400 border-green-400/40",
  declined: "text-red-400 border-red-400/40",
};

export default function Reschedule({ token }) {
  const [bookings, setBookings] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadRequests = () => {
    axios.get(`${API}/reschedule/me`, auth).then((r) => setRequests(r.data.requests || [])).catch(() => {});
  };
  const loadBookings = () => {
    axios.get(`${API}/bookings/me`, auth).then((r) => setBookings(r.data.bookings || [])).catch(() => {});
  };
  useEffect(() => { loadRequests(); loadBookings(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!date) return;
    setTime(null);
    setLoadingSlots(true);
    axios
      .get(`${API}/availability`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => {})
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submit = async () => {
    if (!bookingId) {
      toast.error("Choose which session to move");
      return;
    }
    if (!date || !time) {
      toast.error("Pick a new date and time");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reschedule`, {
        booking_id: bookingId,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        note,
      }, auth);
      toast.success("Reschedule request sent — your coach will confirm it");
      setDate(undefined); setTime(null); setNote(""); setBookingId(null);
      loadRequests();
    } catch {
      toast.error("Could not send request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mt-16 border-t border-white/10 pt-14"
      data-testid="reschedule-section"
    >
      <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em] flex items-center gap-2">
        <CalendarClock className="w-4 h-4" /> Reschedule
      </span>
      <h2 className="font-display font-bold uppercase tracking-tighter text-3xl md:text-5xl mt-3 leading-[0.9]">
        Request a new time
      </h2>
      <p className="text-white/50 mt-3 font-body text-sm max-w-md">Choose a session, pick a new slot, and your coach will confirm the move.</p>

      <div className="grid lg:grid-cols-12 gap-8 mt-8">
        <div className="lg:col-span-7 border border-white/10 p-6">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Session to move</p>
          {bookings.length === 0 ? (
            <p className="text-white/40 text-sm mb-6" data-testid="reschedule-no-bookings">You have no upcoming sessions to reschedule.</p>
          ) : (
            <div className="flex flex-wrap gap-3 mb-6" data-testid="reschedule-booking-picker">
              {bookings.map((b) => (
                <button
                  key={b.id}
                  data-testid={`reschedule-booking-${b.id}`}
                  onClick={() => setBookingId(b.id)}
                  className={`px-4 py-3 text-sm border text-left transition-all ${
                    bookingId === b.id
                      ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                      : "border-white/15 text-white hover:border-white/50"
                  }`}
                >
                  <span className="font-semibold block">{b.date}</span>
                  <span className={`text-xs ${bookingId === b.id ? "text-black/70" : "text-white/50"}`}>{b.time} · {b.session_type}</span>
                </button>
              ))}
            </div>
          )}

          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">New date</p>
          <div className="border border-white/15 p-4 inline-block" data-testid="reschedule-calendar">
            <DayPicker
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={[{ before: new Date() }, isSaturday]}
              weekStartsOn={1}
              styles={{
                caption: { color: "#fff" },
                head_cell: { color: "rgba(255,255,255,0.4)", textTransform: "uppercase", fontSize: 11 },
                day: { color: "#fff", borderRadius: 0 },
                nav_button: { color: "#D4AF37" },
              }}
              modifiersStyles={{
                selected: { background: "#D4AF37", color: "#000", borderRadius: 0 },
                disabled: { color: "rgba(255,255,255,0.2)" },
              }}
            />
          </div>

          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3 mt-6">New time</p>
          {!date && <p className="text-white/40 text-sm">Select a date first.</p>}
          {date && loadingSlots && <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {date && !loadingSlots && slots.length === 0 && (
            <p className="text-white/40 text-sm" data-testid="reschedule-no-slots">No available times for this date — please choose another day.</p>
          )}
          {date && !loadingSlots && slots.length > 0 && (
            <div className="grid grid-cols-4 gap-3" data-testid="reschedule-slots">
              {slots.map((s) => (
                <button
                  key={s.time}
                  data-testid={`reschedule-slot-${s.time}`}
                  disabled={!s.available}
                  onClick={() => setTime(s.time)}
                  className={`py-2.5 text-sm border transition-all flex flex-col items-center justify-center leading-tight ${
                    time === s.time ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                      : s.available ? "border-white/15 text-white hover:border-white/50"
                      : "border-white/5 bg-white/[0.02] text-white/25 cursor-not-allowed"
                  }`}
                >
                  <span className={!s.available ? "line-through" : ""}>{s.time}</span>
                  {!s.available && <span className="text-[9px] uppercase tracking-[0.15em] text-white/55 mt-0.5">Booked</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <label className="text-xs uppercase tracking-[0.15em] text-white/40">Note (optional)</label>
          <textarea
            data-testid="reschedule-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Anything your coach should know?"
            className="w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] transition-colors resize-none placeholder:text-white/25 text-white"
          />
          <button
            data-testid="reschedule-submit"
            onClick={submit}
            disabled={submitting || !bookingId || !date || !time}
            className="mt-6 w-full bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-40"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Request reschedule"}
          </button>

          {requests.length > 0 && (
            <div className="mt-8" data-testid="reschedule-requests">
              <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Your requests</p>
              <div className="space-y-3">
                {requests.map((r) => (
                  <div key={r.id} className="border border-white/10 p-4 flex items-center justify-between gap-3" data-testid={`reschedule-req-${r.id}`}>
                    <div>
                      {r.original_date && (
                        <p className="text-white/40 text-xs mb-0.5">From {r.original_date} · {r.original_time}</p>
                      )}
                      <p className="text-sm text-white">To {r.preferred_date} · {r.preferred_time}</p>
                      {r.note && <p className="text-white/40 text-xs mt-1">{r.note}</p>}
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-1 ${STATUS_STYLE[r.status] || "text-white/50 border-white/20"}`}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}
